import { useEffect, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  description: string;
  action: () => void;
  disabled?: boolean;
  preventDefault?: boolean;
}

export interface KeyboardShortcutConfig {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  onKeyPress?: (key: string) => void;
}

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
  onKeyPress
}: KeyboardShortcutConfig) => {
  const shortcutsRef = useRef(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' ||
                           target.tagName === 'TEXTAREA' ||
                           target.contentEditable === 'true';

      // Skip shortcuts when typing in input fields (unless it's Escape)
      if (isInputField && event.key !== 'Escape') {
        return;
      }

      // Build the key string (including modifiers)
      const keyParts: string[] = [];
      if (event.ctrlKey) keyParts.push('Ctrl');
      if (event.altKey) keyParts.push('Alt');
      if (event.shiftKey) keyParts.push('Shift');
      if (event.metaKey) keyParts.push('Meta');

      // Add the main key
      keyParts.push(event.key);

      const keyString = keyParts.join('+');

      // Find matching shortcut
      const shortcut = shortcutsRef.current.find(
        s => !s.disabled && s.key.toLowerCase() === keyString.toLowerCase()
      );

      if (shortcut) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.action();

        // Call onKeyPress callback if provided
        if (onKeyPress) {
          onKeyPress(shortcut.key);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, onKeyPress]);
};

// Default keyboard shortcuts for the grading page
export const GRADING_SHORTCUTTS = {
  // Navigation
  PREV_STUDENT: {
    key: 'ArrowLeft',
    description: 'Əvvəlki şagird',
    category: 'navigasiya' as const
  },
  NEXT_STUDENT: {
    key: 'ArrowRight',
    description: 'Növbəti şagird',
    category: 'navigasiya' as const
  },
  CLOSE_MODAL: {
    key: 'Escape',
    description: 'Modalı bağla',
    category: 'navigasiya' as const
  },

  // Scoring - Normal mode
  SCORE_0: {
    key: '0',
    description: '0 bal',
    category: 'qiymətləndirmə' as const
  },
  SCORE_1: {
    key: '1',
    description: '1/3 bal',
    category: 'qiymətləndirmə' as const
  },
  SCORE_2: {
    key: '2',
    description: '1/2 bal',
    category: 'qiymətləndirmə' as const
  },
  SCORE_3: {
    key: '3',
    description: '2/3 bal',
    category: 'qiymətləndirmə' as const
  },
  SCORE_4: {
    key: '4',
    description: '1 bal',
    category: 'qiymətləndirmə' as const
  },
  SAVE_AND_NEXT: {
    key: 'Enter',
    description: 'Yadda saxla və növbəti',
    category: 'qiymətləndirmə' as const
  },

  // Tools
  SHOW_SHORTCUTS: {
    key: '?',
    description: 'Qısa yolları göstər',
    category: 'alətlər' as const
  },
  SHOW_QUESTION_IMAGE: {
    key: 'Shift+Q',
    description: 'Sual şəklini göstər',
    category: 'alətlər' as const
  },
  FOCUS_NOTE: {
    key: 'Shift+N',
    description: 'Qeyd dialoquna fokusa al',
    category: 'alətlər' as const
  }
};

export type ShortcutKey = keyof typeof GRADING_SHORTCUTTS;

export const getShortcutKey = (shortcutName: ShortcutKey): string => {
  return GRADING_SHORTCUTTS[shortcutName].key;
};

export const getShortcutDescription = (shortcutName: ShortcutKey): string => {
  return GRADING_SHORTCUTTS[shortcutName].description;
};
